<template>
  <div class="page-wrapper">
    <div class="stepper-header">
      <v-img alt="Step Logo" class="step4-image step-logo"/>
      <span class="reg-titre1" v-html="title"></span>
    </div>

    <div>
      <v-form ref="USER" lazy-validation>
        <div v-if="!displayEtablissement" style="width: 100%; display: flex; flex-direction: column;">

          <div v-if="!verifyingSiret" style="width: 193px;">
            <etablissement-siret
                ref="SIRET"
                v-model:value="siret"
                class="custom-fieldS4"
                :theme="theme"
                :show-missing="showMissing"
                :required="true"
                :context-id="etablissement.id"
                :read-only="false"
                :obj-type="objType"
                :fiche="fiche"
                :section-name="sectionName"
                @status="statusSiret"
            />
          </div>

          <div v-if="!verifyingSiret" style="margin-left: -10px; margin-top: -20px;">
            <v-radio-group
                v-model="representantChoice"
                class="mt-4 custom-radio-label"
                label="Je suis représentant légal de la structure ou je suis habilité par le représentant légal à signer pour le compte de la structure"
                :mandatory="true"
                inline
                @update:model-value="handleChoice"
            >
              <v-radio label="Oui" :value="true" />
              <v-radio label="Non" :value="false" />
            </v-radio-group>
          </div>

          <div v-if="verifyingSiret">
            <img src="@/assets/loading.gif" alt="Chargement..." style="width: 150px; height: 150px;" />
            <p style="font-size: 12px; color: #0A2C2D; font-weight: 400; margin-top: 16px; margin-left: 5px;">
              Nous récupérons vos informations.
            </p>
          </div>

        </div>

        <div v-if="displayEtablissement" class="etablissement-container">
          <transition-group name="fade" tag="div" class="field-columns">

            <div class="column">
              <div v-for="field in visibleLeftFields" :key="field.key" class="fade-field">
                <label :for="field.key" class="field-label">{{ field.label }}</label>
                <input :id="field.key" :value="etablissement[field.key]" class="field-input" readonly />
              </div>
            </div>

            <div class="column">
              <div v-for="field in visibleRightFields" :key="field.key" class="fade-field">
                <label :for="field.key" class="field-label">{{ field.label }}</label>
                <input
                    :id="field.key"
                    :value="
                    field.key === 'ville'
                      ? `${etablissement.ville} (${etablissement.codePostal})`
                      : field.key === 'codeApe'
                        ? `${etablissement.brancheCode} ${etablissement.brancheNom}`
                        : etablissement[field.key]
                  "
                    class="field-input"
                    readonly
                />
              </div>
            </div>

          </transition-group>
        </div>

      </v-form>
    </div>
  </div>

  <div class="action-footer">
    <loader-button
        :label="precedentBtnTxt"
        :outlined="true"
        @callback="previous"
    />
    <loader-button
        :label="suivantBtnTxt"
        :loading="verifyingSiret"
        @callback="next"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from "vue";
import {EditFieldTheme} from "@/framework/base/components/FieldInfo.ts";
import EtablissementSiret from "@/extend/controls/etablissement/EtablissementSiret.ts";
import CrudEtablissement from "@/generated/cruds/CrudEtablissement.ts";
import {Sections} from "@/generated/classes/AppSchema.ts";
import {AppButtons, AppCustoms, RessourceAppButton, RessourceAppCustom} from "@/ressources/globalRessources.ts";
import SiretInscription, {SiretInscriptionAnswser} from "@/generated/classes/SiretInscription.ts";
import {Alerts} from "@/framework/base/helpers/Alerts.ts";
import {storeEtablissement} from "@/generated/stores";

export default defineComponent({
  components: {
    EtablissementSiret,
  },
  extends:  CrudEtablissement,
  props: {
    subscription: {
      type: Object as PropType<SiretInscription>,
      required: true
    },
  },
  data() {
    return {
      representantChoice: false,
      sectionName: Sections.ETABLISSEMENT_IDENTITE,
      visibleLeftFields: [] as any[],
      visibleRightFields: [] as any[],
      verifyingSiret: false,
      displayEtablissement: false,
      leftFields: [
        { key: 'nom', label: 'Nom de l’entreprise' },
        { key: 'adresseLigne1', label: 'Adresse' },
        { key: 'siret', label: 'SIRET' }
      ],
      rightFields: [
        { key: 'codeApe', label: 'Code APE' },
        { key: 'ville', label: 'Ville' }
      ]

    }
  },
  computed: {
    EditFieldTheme() {
      return EditFieldTheme;
    },
    title(): string {
      return RessourceAppCustom(AppCustoms.REGISTER_ETAPE4_LABEL).label;
    },
    suivantBtnTxt(): string {
      return RessourceAppButton(AppButtons.SUIVANT).label;
    },
    precedentBtnTxt(): string {
      return RessourceAppButton(AppButtons.PRECEDENT).label;
    },
  },
  methods: {
    verifySiret(): void {
      this.verifyingSiret = true;
      this.subscription.etablissement.dateCreation = new Date();
      SiretInscription.verifySiret(this.subscription)
          .then((answer: SiretInscriptionAnswser) => {
            if (answer.dialog) {
              Alerts.message(answer.dialog).then(() => {
                this.subscription.etablissement.fromEtablissement(storeEtablissement.getEtablissement(0));
                this.verifyingSiret = false;
              });
            } else if (answer.verified) {
              this.subscription.etablissement.fromEtablissement(answer.etablissement);
              setTimeout(() => {
                this.verifyingSiret = false;
                this.displayEtablissement = true;
                this.revealFields(1000);
              }, 1000);
            } else {
              this.verifyingSiret = false;
            }
          })
          .catch(() => {
            this.verifyingSiret = false;
          });
    },

    previous() {
      this.$emit('previous');
    },
    next() {
      if (this.checkFields()) {
        if (!this.displayEtablissement) {
          this.verifySiret();
        } else {
          this.$emit('next');
        }
      }
    },
    handleChoice(value: boolean | null) {
      if (value != null) {
        this.subscription.etablissement.representantLegal = value;
        this.representantChoice = value;
      }
    },

    revealFields(delay: number = 0) {
      this.visibleLeftFields = []
      this.visibleRightFields = []

      const sequence = [
        { side: 'left', index: 0 },
        { side: 'right', index: 0 },
        { side: 'left', index: 1 },
        { side: 'right', index: 1 },
        { side: 'left', index: 2 }
      ]

      sequence.forEach((step, i) => {
        setTimeout(() => {
          if (step.side === 'left') {
            this.visibleLeftFields.push(this.leftFields[step.index])
          } else {
            this.visibleRightFields.push(this.rightFields[step.index])
          }
        }, i * delay);
      })
    }
  }
})
</script>

<style>
.custom-fieldS4 .v-field input{
  background-color: #F7F8F8 !important;
  border-radius: 8px !important;
}

.custom-radio-label .v-label {
  font-size: 14px;
  font-weight: 500;
  color: #1C7A7D;
}

.custom-radio-label .v-radio {
  margin-right: 24px;
}

.custom-radio-label .v-radio .v-label {
  font-size: 14px;
  color: #2D3539;
  font-weight: 400;
}

.custom-radio-label .v-label {
  white-space: normal !important;
  overflow-wrap: break-word;
  line-height: 1.3;
}


.etablissement-container {
  width: 100%;
  padding-top:32px;
}

.field-columns {
  display: flex;
}

.column {
  display: flex;
  flex-direction: column;
  width: 300px;
}

.fade-field {
  margin-bottom: 16px;
  width: 226px;
}

.field-label {
  font-size: 12px;
  font-weight: 500;
  color: #1C7A7D;
  display: block;
  width: 226px;
}

.field-input {
  width: 226px;
  font-size: 14px;
  border: none;
  outline: none;
  background-color: transparent;
  color: #09292A;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.8s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}



</style>